/**
 * Playful Theme Context Provider
 *
 * Manages the active playful theme and sound preference.
 * Follows the same pattern as ThemeContext and SizingContext:
 * - Reads initial value via getInitialPreferences()
 * - Subscribes to usePreferencesQuery() for live updates
 * - Injects CSS keyframes via a <style> element
 * - Respects prefers-reduced-motion
 */

import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { PlayfulThemeId } from './identifiers';
import { PlayfulThemeDefinition } from './types';
import { getPlayfulTheme, hasPlayfulTheme, PlayfulThemeName } from './registry';
import { getInitialPreferences } from '@/hooks/usePreferenceManager';
import { usePreferencesQuery } from '@/hooks/queries';
import { logger } from '@/utils/logger';
import { setSoundEnabled as setReactSoundsEnabled } from 'react-sounds';

export interface PlayfulContextValue {
  /** Current playful theme ID */
  playfulTheme: PlayfulThemeName;
  /** Set the playful theme */
  setPlayfulTheme: (theme: PlayfulThemeName) => void;
  /** Current theme definition */
  themeDefinition: PlayfulThemeDefinition;
  /** Whether sounds are enabled */
  soundEnabled: boolean;
  /** Toggle sound on/off */
  setSoundEnabled: (enabled: boolean) => void;
  /** Whether reduced motion is active (forces Off theme) */
  reducedMotion: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components -- exported context alongside provider
export const PlayfulContext = createContext<PlayfulContextValue | undefined>(
  undefined
);

const DEFAULT_PLAYFUL_THEME = PlayfulThemeId.OFF;

export const PlayfulProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const styleRef = useRef<HTMLStyleElement | null>(null);

  // Detect prefers-reduced-motion
  const [reducedMotion, setReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Read initial playful theme from cached preferences
  const [playfulTheme, setPlayfulThemeState] = useState<PlayfulThemeName>(
    () => {
      const stored = getInitialPreferences()?.playful_theme;
      if (stored && hasPlayfulTheme(stored)) {
        return stored as PlayfulThemeName;
      }
      return DEFAULT_PLAYFUL_THEME;
    }
  );

  const [soundEnabled, setSoundEnabledState] = useState(() => {
    const stored = getInitialPreferences()?.playful_sound;
    return stored === 'on'; // Default to off — opt-in only
  });

  // Subscribe to preference changes via React Query cache
  const { data: queryPreferences } = usePreferencesQuery();

  useEffect(() => {
    const stored = queryPreferences?.playful_theme;
    if (stored && hasPlayfulTheme(stored)) {
      logger.ui.debug(
        '[PlayfulContext] Updating playful theme from query cache:',
        { theme: stored }
      );
      setPlayfulThemeState(stored as PlayfulThemeName);
    }
    const soundPref = queryPreferences?.playful_sound;
    if (soundPref !== undefined) {
      setSoundEnabledState(soundPref === 'on');
    }
  }, [queryPreferences]);

  // Resolve effective theme (force Off when reduced motion)
  const effectiveTheme = reducedMotion ? PlayfulThemeId.OFF : playfulTheme;
  const themeDefinition = getPlayfulTheme(effectiveTheme);

  // Inject CSS keyframes into a <style> element
  useEffect(() => {
    if (!styleRef.current) {
      const style = document.createElement('style');
      style.setAttribute('data-playful-keyframes', 'true');
      document.head.appendChild(style);
      styleRef.current = style;
    }

    const css = themeDefinition.keyframes.map(kf => kf.css).join('\n');
    styleRef.current.textContent = css;

    return () => {
      if (styleRef.current) {
        styleRef.current.textContent = '';
      }
    };
  }, [themeDefinition]);

  // Cleanup style element on unmount
  useEffect(() => {
    return () => {
      if (styleRef.current) {
        styleRef.current.remove();
        styleRef.current = null;
      }
    };
  }, []);

  const setPlayfulTheme = (theme: PlayfulThemeName) => {
    setPlayfulThemeState(theme);
  };

  const setSoundEnabled = (enabled: boolean) => {
    setSoundEnabledState(enabled);
    setReactSoundsEnabled(enabled);
  };

  // Sync react-sounds enabled state on mount and when soundEnabled changes
  useEffect(() => {
    setReactSoundsEnabled(soundEnabled);
  }, [soundEnabled]);

  return (
    <PlayfulContext.Provider
      value={{
        playfulTheme: effectiveTheme,
        setPlayfulTheme,
        themeDefinition,
        soundEnabled,
        setSoundEnabled,
        reducedMotion,
      }}
    >
      {children}
    </PlayfulContext.Provider>
  );
};
